import { useState, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

export const FORECAST_TIMESCALE_TABS = ['24HR', '48HR', 'WEEK'];
export const DASHBOARD_TIMESCALE_TABS = FORECAST_TIMESCALE_TABS;

const DEFAULT_CUSTOM_RANGE_DAYS = 7;

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const TimescaleTabs = ({
  tabs,
  activeTab,
  setActiveTab,
  showDatePicker = false,
  customRange = null,
  setCustomRange = () => {},
  maxRangeDays = DEFAULT_CUSTOM_RANGE_DAYS,
  limitToLastDays = null,
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  // Picker UI state — mirrors `customRange` from the parent but also tracks
  // the half-selected state (start only) before the user picks an end date.
  const [startDate, setStartDate] = useState(customRange?.start ?? null);
  const [endDate, setEndDate] = useState(customRange?.end ?? null);
  const wrapperRef = useRef(null);

  // Keep the picker in sync if `customRange` is changed by the parent
  // (e.g. cleared when a tab button is clicked elsewhere).
  useEffect(() => {
    setStartDate(customRange?.start ?? null);
    setEndDate(customRange?.end ?? null);
  }, [customRange]);

  const today = useMemo(() => new Date(), []);
  const todayStart = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), today.getDate()),
    [today],
  );
  const earliestSelectable = useMemo(() => {
    if (limitToLastDays != null && limitToLastDays > 0) {
      return addDays(todayStart, -(limitToLastDays - 1));
    }
    return new Date(today.getFullYear() - 2, today.getMonth(), today.getDate());
  }, [today, todayStart, limitToLastDays]);
  const latestSelectable = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999),
    [today],
  );

  // While the user has picked one end of the range but not the other, clamp
  // selectable dates so the span can never exceed maxRangeDays.
  const effectiveMinDate = useMemo(() => {
    if (startDate && !endDate) {
      const minFromStart = addDays(startDate, -(maxRangeDays - 1));
      return minFromStart > earliestSelectable ? minFromStart : earliestSelectable;
    }
    return earliestSelectable;
  }, [startDate, endDate, earliestSelectable, maxRangeDays]);

  const effectiveMaxDate = useMemo(() => {
    if (startDate && !endDate) {
      const maxFromStart = addDays(startDate, maxRangeDays - 1);
      return maxFromStart < latestSelectable ? maxFromStart : latestSelectable;
    }
    return latestSelectable;
  }, [startDate, endDate, latestSelectable, maxRangeDays]);

  useEffect(() => {
    if (!isPickerOpen) return undefined;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPickerOpen]);

  const handleRangeChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    // Only promote the range to the parent once both ends are selected; the
    // parent treats a non-null `customRange` as "override tab-based charts".
    if (start && end) {
      let normalizedStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      let normalizedEnd = new Date(
        end.getFullYear(),
        end.getMonth(),
        end.getDate(),
        23,
        59,
        59,
        999,
      );

      if (normalizedStart < earliestSelectable) {
        normalizedStart = new Date(earliestSelectable);
      }
      if (normalizedEnd > latestSelectable) {
        normalizedEnd = new Date(latestSelectable);
      }
      if (normalizedEnd < normalizedStart) {
        normalizedEnd = new Date(normalizedStart);
        normalizedEnd.setHours(23, 59, 59, 999);
      }

      const spanDays = Math.floor(
        (normalizedEnd.getTime() - normalizedStart.getTime()) / 86400000,
      ) + 1;
      if (spanDays > maxRangeDays) {
        normalizedEnd = addDays(normalizedStart, maxRangeDays - 1);
        normalizedEnd.setHours(23, 59, 59, 999);
        if (normalizedEnd > latestSelectable) {
          normalizedEnd = new Date(latestSelectable);
          normalizedStart = addDays(normalizedEnd, -(maxRangeDays - 1));
          normalizedStart.setHours(0, 0, 0, 0);
          if (normalizedStart < earliestSelectable) {
            normalizedStart = new Date(earliestSelectable);
          }
        }
      }

      setCustomRange({ start: normalizedStart, end: normalizedEnd });
      setIsPickerOpen(false);
    }
  };

  const handleTabClick = (index) => {
    setActiveTab(index);
    // Switching to a built-in tab leaves any prior custom range behind so the
    // user can re-open the picker and see it again, but it no longer drives
    // the chart selection.
    if (showDatePicker && customRange) {
      setCustomRange(null);
    }
  };

  const isCustomRangeActive = showDatePicker && Boolean(customRange?.start && customRange?.end);

  return (
    <div className='timescale'>
      {tabs.map((tab, index) => (
        <button
          key={index}
          onClick={() => handleTabClick(index)}
          className={`${ activeTab === index && !isCustomRangeActive ? 'active' : '' }`}
        >
          {tab}
        </button>
      ))}
      {showDatePicker && (
        <div className='timescale-datepicker' ref={wrapperRef}>
          <button
            type='button'
            aria-label='Select date range'
            aria-haspopup='dialog'
            aria-expanded={isPickerOpen}
            className={isCustomRangeActive ? 'active' : ''}
            onClick={() => setIsPickerOpen((prev) => !prev)}
          >
            <FontAwesomeIcon icon={faCalendar} />
          </button>
          {isPickerOpen && (
            <div className='timescale-datepicker__popover'>
              <DatePicker
                selected={startDate}
                onChange={handleRangeChange}
                startDate={startDate}
                endDate={endDate}
                minDate={effectiveMinDate}
                maxDate={effectiveMaxDate}
                selectsRange
                swapRange
                inline
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimescaleTabs;
